import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { CheckCircleOutline, ErrorOutline, AccessTimeOutlined, SyncOutlined } from "@mui/icons-material";
import moment from "moment";

import { CardLayout, MTable, SelectField } from "../v3";
import { AdminService, ProjectService, TaskService } from "../../services";
import { EXPORT_TASK, VIEW_ASSIGNED_TASK, VIEW_ALL_TASK } from "../../constants";
import { getTaskTableColumns } from "../../pages/task/constants";
import { derivePdfColumns, getPrintableRows, useHasPermission } from "../../utils";
import { handlePdfDownload, Listing } from "../pdf";
import { usePersistedTableState } from "../../hooks/usePersistedTableState";

export const TaskList = ({ companyId = null, totalCount, setTotalCount = () => {}, reloadData = 0, projectId = null }) => {
  const [task, setTask] = useState([]);
  const [assignee, setAssignee] = useState([]);
  const [projects, setProjects] = useState([]);
  // Remembered per visit: Back restores these, forward navigation starts from the defaults.
  const {
    filter: selectedFilter,
    setFilter: setSelectedFilter,
    searchText,
    setSearchText,
    sortModel,
    setSortModel,
    paginationModel: paginationState,
    setPaginationModel: setPaginationState,
    dropdowns,
    setDropdowns,
  } = usePersistedTableState({
    tableId: "tasks",
    defaults: {
      filter: "TODO",
      dropdowns: { assignees: [], assigneeIds: "" },
      sortModel: [{ field: "updatedAt", sort: "desc" }],
    },
    filterValues: ["TODO", "INPROGRESS", "DONE", "OVERDUE"],
  });
  const selectedAssignee = dropdowns.assignees;
  const selectedAssigneeIds = dropdowns.assigneeIds;
  const [loading, setLoading] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);

  const { user } = useSelector((state) => state.user);
  const navigate = useNavigate();

  const hasTaskExport = useHasPermission(EXPORT_TASK);
  const canViewAllTask = useHasPermission(VIEW_ALL_TASK);
  const canViewAssignedTask = useHasPermission(VIEW_ASSIGNED_TASK);

  const fetchData = useCallback(
    async (taskFilters = {}) => {
      setLoading(true);
      try {
        const { data: tasks } = await TaskService.getTaskList({
          ...taskFilters,
          includeProgress: true,
          ...(companyId !== null && { companyId }),
        });

        const assigneeIds = Array.from(new Set(tasks?.rows?.map((item) => item.assignedToId)));
        if (assigneeIds.length > 0) {
          const { data: assignees } = await AdminService.getUserList({
            ids: assigneeIds.join(","),
            limit: assigneeIds.length,
            includeDeleted: true,
          });
          setAssignee(assignees?.rows);
        }

        const projectIds = Array.from(new Set(tasks?.rows?.map((item) => item.projectId)));
        if (projectIds.length > 0) {
          const { data: projects } = await ProjectService.getProjectList({
            ids: projectIds.join(","),
            limit: projectIds.length,
            includeDeleted: true,
          });
          setProjects(projects?.rows);
        }
        setTask(tasks?.rows);
        setTotalCount(tasks?.count);
      } catch (error) {
        toast.error("Failed to fetch tasks");
        console.error("🚀 ~ fetchData ~ error:", error);
      } finally {
        setLoading(false);
      }
    },
    [setTotalCount, companyId],
  );

  useEffect(() => {
    const isOverdue = selectedFilter === "OVERDUE";
    // Merge base, UI and permission filters in one place so pagination/search/sort share the same request logic.
    const permissionAssignedToIds = !canViewAllTask && canViewAssignedTask && user?.id ? String(user.id) : undefined;
    const mergedAssignedToIds = permissionAssignedToIds ?? (selectedAssigneeIds || undefined);

    fetchData({
      sortBy: sortModel[0]?.field,
      sortOrder: sortModel[0]?.sort,
      page: paginationState.page + 1,
      limit: paginationState.pageSize,
      status: projectId ? null : isOverdue ? "TODO,INPROGRESS" : selectedFilter,
      ...(isOverdue && { endDateTo: moment().subtract(1, "days").format("YYYY-MM-DD") }),
      name: searchText,
      projectId,
      assignedToIds: mergedAssignedToIds,
    });
  }, [
    fetchData,
    sortModel,
    paginationState,
    selectedFilter,
    searchText,
    projectId,
    reloadData,
    canViewAllTask,
    canViewAssignedTask,
    user?.id,
    selectedAssigneeIds,
  ]);

  const handleFilterChange = (filterValue) => {
    setSelectedFilter(filterValue);
    // Reset pagination when top-level filter changes to avoid stale page index on a narrowed dataset.
    setPaginationState((prev) => ({ ...prev, page: 0 }));
  };

  const handleSearchChange = (searchValue) => {
    setSearchText(searchValue);
  };

  const handlePaginationChange = (model) => {
    setPaginationState(model);
  };

  const handleAssigneeFilterChange = (value) => {
    const selected = Array.isArray(value) ? value : [];
    setDropdowns({
      // Only what the picker needs to match and label an option: these are remembered in sessionStorage.
      assignees: selected.map(({ id, firstName, lastName }) => ({ id, firstName, lastName })),
      assigneeIds: selected.map((assigneeItem) => assigneeItem.id).join(","),
    });
    setPaginationState((prev) => ({ ...prev, page: 0 }));
  };

  const handleDownloadPdfClick = async () => {
    setIsPdfGenerating(true);
    try {
      let titleSuffix = "";

      if (isProjectPage) {
        const project = projects.find((p) => p.id === Number(projectId));
        titleSuffix = project?.name || "Project";
      } else {
        titleSuffix = pdfTitleMap[selectedFilter] || "Tasks";
      }
      const pdfColumnHeaders = derivePdfColumns(getTaskTableColumns({ isProjectPage }));
      const printableRows = getPrintableRows(rows);

      const pdfDocument = (
        <Listing
          title={`Tasks : ${titleSuffix}`}
          columnHeaders={pdfColumnHeaders}
          tableRows={printableRows}
          company={user?.company}
        />
      );

      await handlePdfDownload({
        document: pdfDocument,
        pdfTitle: `Tasks : ${titleSuffix}`,
      });
    } catch (error) {
      toast.error("PDF generation failed");
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const onViewClick = (params, event) => {
    navigate(`/tasks/${params.row.id}/progress`);
  };

  const filterOptions = [
    { label: "To Do", value: "TODO", icon: <AccessTimeOutlined /> },
    { label: "In Progress", value: "INPROGRESS", icon: <SyncOutlined /> },
    // { label: "On Hold", value: "ON_HOLD", icon: <PauseCircleOutline /> },
    { label: "Done", value: "DONE", icon: <CheckCircleOutline /> },
    // { label: "Cancelled", value: "CANCELLED", icon: <CancelOutlined /> },
    { label: "Overdue", value: "OVERDUE", icon: <ErrorOutline /> },
  ];

  const pdfTitleMap = {
    TODO: "To Do",
    INPROGRESS: "In Progress",
    DONE: "Done",
    OVERDUE: "Overdue",
  };

  const rows = task?.map((item, index) => {
    const user = assignee?.find((a) => a.id === item.assignedToId);
    const project = projects?.find((p) => p.id === item.projectId);

    // calculate total quantity from taskProgresses
    const totalProgressQuantity = item.taskProgresses
      ? item.taskProgresses.reduce((sum, progress) => sum + progress.quantity, 0)
      : 0;

    const percentage = item.quantity ? Math.min((totalProgressQuantity / item.quantity) * 100, 100) : 0;

    return {
      sl: index + 1 + paginationState.page * paginationState.pageSize,
      id: item.id,
      name: item.name,
      assignedTo: user ? `${user.firstName} ${user.lastName}` : "Not assigned",
      projectId: project?.name || "-",
      quantity: `${item?.quantity}(${item?.unitOfMeasure})`,
      taskQuantity: item?.quantity,
      taskProgressQuantity: totalProgressQuantity,
      progress: `${totalProgressQuantity}/${item.quantity} (${percentage.toFixed(0)}%)`,
    };
  });

  const isProjectPage = !!projectId;
  // Keep assignee filtering on the task listing experience, not project-scoped task view.
  const shouldShowAssigneeFilter = canViewAllTask && !isProjectPage;
  const dropdownFilters = [];

  if (shouldShowAssigneeFilter) {
    dropdownFilters.push({
      key: "assigneeId",
      label: "Assignee",
      value: selectedAssignee,
      minWidth: 180,
      render: () => (
        <SelectField
          limitTags={1}
          multiSelect
          label="Assignee"
          value={selectedAssignee}
          getData={AdminService.getUserList}
          getDataStaticFilters={{ type: "USER", active: true, ...(companyId !== null && { companyId }) }}
          optionValueFields={["firstName", "lastName"]}
          onChange={handleAssigneeFilterChange}
          style={{
            minWidth: 190,
            maxWidth: 240,
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#fff",
              borderRadius: "10px",
              height: 40,
              paddingRight: "65px !important",
              "& fieldset": {
                borderRadius: "10px",
              },
            },
            "& .MuiTextField-root": {
              marginBottom: "0 !important",
            },
            "& .MuiAutocomplete-tag": {
              maxWidth: "100%",
            },
            "& .MuiChip-label": {
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
          }}
        />
      ),
    });
  }

  return (
    <CardLayout>
      <MTable
        // Filter data
        selectedFilter={selectedFilter}
        filterOptions={filterOptions}
        onFilterChange={handleFilterChange}
        showFilterButton={!isProjectPage}
        // Search data
        placeholder="Search task"
        searchText={searchText}
        onSearchChange={handleSearchChange}
        showSearchBar
        dropdownFilters={dropdownFilters}
        // Export data
        showExportButton={hasTaskExport}
        isPdfGenerating={isPdfGenerating}
        onExportPdfClick={handleDownloadPdfClick}
        // Table data
        columnHeaders={getTaskTableColumns({ isProjectPage: isProjectPage })}
        tableRows={rows}
        rowCount={totalCount || 0}
        loading={loading}
        sortModel={sortModel}
        onSortChange={setSortModel}
        paginationModel={paginationState}
        onPaginationChange={handlePaginationChange}
        onRowClick={onViewClick}
      />
    </CardLayout>
  );
};
